// src/components/ui/CalendlyWidget.jsx
import React, { useEffect, useRef } from 'react';

/**
 * A component that embeds a Calendly scheduling widget
 * 
 * @param {Object} props - Component props
 * @param {string} props.calendarUrl - Your Calendly URL
 * @param {string} props.height - Height of the widget (default: '500px')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.hideCookieBanner - Whether to hide the cookie banner (default: true)
 * @param {boolean} props.hideGdprBanner - Whether to hide the GDPR banner (default: true)
 * @param {string} props.primaryColor - Primary color for the widget (default: '8e293e' - secondary brand color)
 */
const CalendlyWidget = ({ 
  calendarUrl, 
  height = '500px', 
  className = '',
  hideCookieBanner = true,
  hideGdprBanner = true,
  primaryColor = '8e293e'
}) => {
  // Use ref to keep track of the widget container
  const widgetContainerRef = useRef(null);

  // Build URL with parameters to hide banners and set colors
  const buildCalendlyUrl = () => {
    const url = new URL(calendarUrl);
    
    if (hideCookieBanner) {
      url.searchParams.set('hide_cookie_banner', '1');
    }
    
    if (hideGdprBanner) {
      url.searchParams.set('hide_gdpr_banner', '1');
    }
    
    if (primaryColor) {
      url.searchParams.set('primary_color', primaryColor);
    }
    
    return url.toString();
  };
  
  useEffect(() => {
    // Function to initialize Calendly
    const initializeCalendly = () => {
      if (window.Calendly && widgetContainerRef.current) {
        try {
          // Use the correct method name with proper parameters
          window.Calendly.initInlineWidget({
            url: buildCalendlyUrl(),
            parentElement: widgetContainerRef.current,
            prefill: {},
            utm: {}
          });
          console.log('Calendly widget initialized successfully');
        } catch (error) {
          console.error('Error initializing Calendly widget:', error);
        }
      }
    };
    
    // Load the Calendly script if it's not already loaded
    if (!window.Calendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = initializeCalendly;
      
      document.body.appendChild(script);
      
      // Cleanup function to remove script when component unmounts
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    } else {
      // If script is already loaded, initialize the widget
      initializeCalendly();
    }
  }, [calendarUrl, hideCookieBanner, hideGdprBanner, primaryColor]);

  return (
    <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
      <div 
        ref={widgetContainerRef}
        className="calendly-inline-widget" 
        style={{ minWidth: '320px', height }}
      />
    </div>
  );
};

export default CalendlyWidget;